import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import './StructuredAnalysisView.css';

// Helper function to safely parse JSON strings
const repairCommonLLMJsonIssues = (text) => {
  if (typeof text !== 'string') return text;

  const lines = text.split('\n');
  const repairedLines = lines.map((line) => {
    const match = line.match(/^(\s*"[^"]+"\s*:\s*")(.*)("\s*,?\s*)$/);
    if (!match) return line;

    const [, prefix, value, suffix] = match;
    const escapedValue = value.replace(/(?<!\\)"/g, '\\"');
    return `${prefix}${escapedValue}${suffix}`;
  });

  return repairedLines.join('\n');
};

const parseJSONField = (field) => {
  if (field && typeof field === 'object') return field;
  if (typeof field !== 'string') return null;

  let text = field.trim();
  const fencedMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fencedMatch && fencedMatch[1]) {
    text = fencedMatch[1].trim();
  }

  try {
    const parsed = JSON.parse(text);
    if (typeof parsed === 'string') {
      try {
        return JSON.parse(parsed);
      } catch {
        return null;
      }
    }
    return parsed;
  } catch {
    const repaired = repairCommonLLMJsonIssues(text);
    if (repaired !== text) {
      try {
        const repairedParsed = JSON.parse(repaired);
        if (typeof repairedParsed === 'string') {
          try {
            return JSON.parse(repairedParsed);
          } catch {
            return null;
          }
        }
        return repairedParsed;
      } catch {
        // continue with object extraction fallback
      }
    }

    // Try to extract the first JSON object from a mixed string
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return null;

    const extracted = jsonMatch[0];
    try {
      return JSON.parse(extracted);
    } catch {
      const repairedExtracted = repairCommonLLMJsonIssues(extracted);
      if (repairedExtracted === extracted) return null;
      try {
        return JSON.parse(repairedExtracted);
      } catch {
        return null;
      }
    }
  }
};

const StructuredAnalysisView = ({ analysis }) => {
  const [expandedSections, setExpandedSections] = useState({
    verdict: true,
    metadata: true,
    visual: true,
    visualSuggestions: true,
    competitiveLandscape: true,
    visualBenchmark: true,
  });
  const [activeSection, setActiveSection] = useState('verdict');

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Parse the analysis results - handle both nested structures
  const analysisData = analysis?.analysis_results?.analysis || 
                       analysis?.analysis_results || 
                       analysis?.analysis || 
                       {};
  const visualContext = analysis?.analysis_results?.visual_context || analysis?.visual_context || {};

  /* persona_reactors, clarity_engagement, claims_risk, cta_alignment,
     trigger_sensitivity and research are deliberately not read here. They still
     run and are still stored on the analysis, but the backend now hands them to
     the retention agent, which merges them into one score and one action list.
     Reading them again here would put the same findings back on screen twice. */
  const visualSuggestionsData = parseJSONField(analysisData.visual_suggestions || analysis?.analysis_results?.visual_suggestions);
  const metadataData = parseJSONField(analysisData.metadata || analysis?.analysis_results?.metadata);
  const competitiveLandscapeData = parseJSONField(analysisData.competitive_landscape || analysis?.analysis_results?.competitive_landscape);
  const visualBenchmarkData = parseJSONField(analysisData.visual_benchmark || analysis?.analysis_results?.visual_benchmark);
  const structureData = parseJSONField(analysisData.retention_structure || analysis?.analysis_results?.retention_structure);
  const generatedThumbnail = analysisData?.generated_assets?.thumbnail || analysis?.analysis_results?.generated_assets?.thumbnail || null;
  const agentErrors = (analysisData && analysisData.agent_errors) || analysis?.analysis_results?.agent_errors || null;
  const agentErrorEntries = agentErrors && typeof agentErrors === 'object'
    ? Object.entries(agentErrors).filter(([, message]) => message)
    : [];
  const detectedObjects = visualContext.detected_objects || analysisData.detected_objects || [];
  const detectedActions = visualContext.detected_actions || analysisData.detected_actions || [];
  const monotonyScore = visualContext.monotony_score ?? analysisData.monotony_score;
  const engagementScore = visualContext.engagement_score ?? analysisData.engagement_score;
  const suggestions = visualContext.suggestions || analysisData.suggestions || [];
  const engagementTimeline = visualContext.engagement_timeline || [];
  const expectedRetentionTimeline = visualContext.expected_retention_timeline || [];
  const dropOffPoints = visualContext.drop_off_points || [];

  const visualEngagementRisk = visualContext.engagement_risk;
  const hasMetadata = Boolean(metadataData?.metadata || generatedThumbnail);
  const hasVisual = Boolean(
    detectedObjects.length ||
    detectedActions.length ||
    typeof monotonyScore === 'number' ||
    typeof engagementScore === 'number' ||
    suggestions.length ||
    expectedRetentionTimeline.length ||
    engagementTimeline.length
  );
  const hasVisualSuggestions = Boolean(visualSuggestionsData?.visual_suggestions?.length);
  const hasCompetitiveLandscape = Boolean(
    competitiveLandscapeData &&
    (Array.isArray(competitiveLandscapeData.similar_sources) ||
      Array.isArray(competitiveLandscapeData.content_gaps) ||
      Array.isArray(competitiveLandscapeData.differentiation_opportunities))
  );
  const hasVisualBenchmark = Boolean(
    visualBenchmarkData &&
    (Array.isArray(visualBenchmarkData.benchmarked_videos) ||
      Array.isArray(visualBenchmarkData.visual_gaps) ||
      Array.isArray(visualBenchmarkData.production_quick_wins))
  );
  // The computed score is the gate: a run whose scores could not be read is a
  // failed run, and the backend leaves it unscored precisely so it does not
  // render as a real result here.
  const hasStructure = typeof structureData?.score?.normalized_score === 'number';

  const sectionItems = useMemo(
    () => [
      { id: 'verdict', label: 'The Verdict', visible: hasStructure },
      { id: 'metadata', label: 'Generated Metadata', visible: hasMetadata },
      { id: 'visual', label: 'Visual & Multimodal', visible: hasVisual },
      { id: 'visualSuggestions', label: 'Visual Suggestions', visible: hasVisualSuggestions },
      { id: 'competitiveLandscape', label: 'Competitive Landscape', visible: hasCompetitiveLandscape },
      { id: 'visualBenchmark', label: 'Visual Benchmark', visible: hasVisualBenchmark },
    ].filter((item) => item.visible),
    [hasStructure, hasMetadata, hasVisual, hasVisualSuggestions, hasCompetitiveLandscape, hasVisualBenchmark]
  );

  useEffect(() => {
    if (!sectionItems.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visibleEntries.length > 0) {
          const sectionId = visibleEntries[0].target.getAttribute('data-section-id');
          if (sectionId) {
            setActiveSection(sectionId);
          }
        }
      },
      {
        root: null,
        rootMargin: '-25% 0px -55% 0px',
        threshold: [0.2, 0.4, 0.6],
      }
    );

    const sectionElements = sectionItems
      .map((item) => document.getElementById(`section-${item.id}`))
      .filter(Boolean);

    sectionElements.forEach((element) => observer.observe(element));

    if (sectionItems[0]?.id) {
      setActiveSection(sectionItems[0].id);
    }

    return () => {
      sectionElements.forEach((element) => observer.unobserve(element));
      observer.disconnect();
    };
  }, [sectionItems]);

  const handleSidebarClick = (sectionId) => {
    const sectionElement = document.getElementById(`section-${sectionId}`);
    if (!sectionElement) return;
    sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const getRiskClass = (value) => {
    const normalized = String(value || '').toLowerCase();
    if (normalized === 'high' || Number(value) >= 8) return 'risk-high';
    if (normalized === 'medium' || Number(value) >= 5) return 'risk-medium';
    if (normalized === 'low' || Number(value) > 0) return 'risk-low';
    return 'risk-unknown';
  };

  const retentionPoints = expectedRetentionTimeline.map((point, idx) => ({
    x: idx,
    y: Number(point.expected_retention_percent || 0),
    timestamp: point.timestamp,
  }));

  const retentionPolylinePoints = (() => {
    if (retentionPoints.length < 2) return '';
    const width = 100;
    const height = 100;
    return retentionPoints
      .map((point, idx) => {
        const x = (idx / Math.max(1, retentionPoints.length - 1)) * width;
        const y = height - Math.max(0, Math.min(100, point.y));
        return `${x},${y}`;
      })
      .join(' ');
  })();

  const renderMetadataSection = () => {
    const generatedMetadata = metadataData?.metadata;
    if (!generatedMetadata && !generatedThumbnail) return null;

    const thumbnailSuggestion = generatedMetadata?.thumbnail_suggestion || {};

    return (
      <div className="section analysis-section" id="section-metadata" data-section-id="metadata">
        <div className="section-header" onClick={() => toggleSection('metadata')}>
          <h2>Generated Metadata</h2>
          <span className="toggle-icon" aria-hidden="true">{expandedSections.metadata ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
        </div>

        {expandedSections.metadata && (
          <div className="section-content">
            {generatedMetadata.title && (
              <div className="metadata-block">
                <h3>Title</h3>
                <p>{generatedMetadata.title}</p>
              </div>
            )}

            {generatedMetadata.summary_headline && (
              <div className="metadata-block">
                <h3>Summary Headline</h3>
                <p>{generatedMetadata.summary_headline}</p>
              </div>
            )}

            {generatedMetadata.description && (
              <div className="metadata-block">
                <h3>Description</h3>
                <p>{generatedMetadata.description}</p>
              </div>
            )}

            {Array.isArray(generatedMetadata.tags) && generatedMetadata.tags.length > 0 && (
              <div className="metadata-block">
                <h3>Tags</h3>
                <div className="metadata-chip-list">
                  {generatedMetadata.tags.map((tag, i) => (
                    <span key={`${tag}-${i}`} className="metadata-chip">{tag}</span>
                  ))}
                </div>
              </div>
            )}

            {Array.isArray(generatedMetadata.suggested_hashtags) && generatedMetadata.suggested_hashtags.length > 0 && (
              <div className="metadata-block">
                <h3>Suggested Hashtags</h3>
                <div className="metadata-chip-list">
                  {generatedMetadata.suggested_hashtags.map((hashtag, i) => (
                    <span key={`${hashtag}-${i}`} className="metadata-chip hashtag-chip">{hashtag}</span>
                  ))}
                </div>
              </div>
            )}

            {(thumbnailSuggestion.headline || thumbnailSuggestion.visual_style || thumbnailSuggestion.subject_focus || thumbnailSuggestion.text_overlay || thumbnailSuggestion.color_direction) && (
              <div className="metadata-block thumbnail-block">
                <h3>Thumbnail Suggestion</h3>
                <ul>
                  {thumbnailSuggestion.headline && <li><strong>Headline:</strong> {thumbnailSuggestion.headline}</li>}
                  {thumbnailSuggestion.subject_focus && <li><strong>Subject Focus:</strong> {thumbnailSuggestion.subject_focus}</li>}
                  {thumbnailSuggestion.text_overlay && <li><strong>Text Overlay:</strong> {thumbnailSuggestion.text_overlay}</li>}
                  {thumbnailSuggestion.visual_style && <li><strong>Visual Style:</strong> {thumbnailSuggestion.visual_style}</li>}
                  {thumbnailSuggestion.color_direction && <li><strong>Color Direction:</strong> {thumbnailSuggestion.color_direction}</li>}
                </ul>
              </div>
            )}

            {generatedThumbnail?.signed_url && (
              <div className="metadata-block thumbnail-block">
                <h3>Generated Thumbnail</h3>
                <img
                  src={generatedThumbnail.signed_url}
                  alt="Generated workspace thumbnail"
                  style={{ width: '100%', maxWidth: '420px', borderRadius: 'var(--sb-corner)', border: '1px solid var(--sb-line)' }}
                />
                <p style={{ marginTop: '8px' }}>
                  <strong>Model:</strong> {generatedThumbnail.model || 'unknown'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
    // Render Visual Suggestions Section
    const renderVisualSuggestionsSection = () => {
      if (!visualSuggestionsData?.visual_suggestions || !visualSuggestionsData.visual_suggestions.length) return null;
      return (
        <div className="section analysis-section" id="section-visualSuggestions" data-section-id="visualSuggestions">
          <div className="section-header" onClick={() => toggleSection('visualSuggestions')}>
            <h2>Visual Suggestions</h2>
            <span className="toggle-icon" aria-hidden="true">{expandedSections.visualSuggestions ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
          </div>
          {expandedSections.visualSuggestions && (
            <div className="section-content">
              <ul className="visual-suggestions-list">
                {visualSuggestionsData.visual_suggestions.map((item, i) => (
                  <li key={i} className="visual-suggestion-item">
                    <strong>Time:</strong> {item.timestamp} &nbsp;|&nbsp; <strong>Scene:</strong> {item.scene_index} <br />
                    <strong>Suggestion:</strong> {item.suggestion}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    };
  // Render Multimodal/Visual Analysis Section
  const renderVisualSection = () => {
    if (
      !detectedObjects.length &&
      !detectedActions.length &&
      typeof monotonyScore !== 'number' &&
      typeof engagementScore !== 'number' &&
      !suggestions.length &&
      !expectedRetentionTimeline.length &&
      !engagementTimeline.length
    ) {
      return null;
    }

    const firstRetention = expectedRetentionTimeline[0]?.expected_retention_percent ?? 100;
    const lastRetention = expectedRetentionTimeline[expectedRetentionTimeline.length - 1]?.expected_retention_percent ?? firstRetention;
    const estimatedDropPercent = Math.max(0, Number(firstRetention) - Number(lastRetention));

    return (
      <div className="section analysis-section" id="section-visual" data-section-id="visual">
        <div className="section-header" onClick={() => toggleSection('visual')}>
          <h2>Visual & Multimodal Analysis</h2>
          <span className="toggle-icon" aria-hidden="true">{expandedSections.visual ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
        </div>
        {expandedSections.visual && (
          <div className="section-content">
            {/* Only the reading taken from the footage itself. Clarity and
                trigger risk used to sit here too; they are findings about the
                script, so they now reach the creator once, as the points the
                verdict's score lost. */}
            {visualEngagementRisk && (
              <div className="risk-flags-row">
                <div className={`risk-flag ${getRiskClass(visualEngagementRisk)}`}>
                  Engagement Risk: {String(visualEngagementRisk).toUpperCase()}
                </div>
              </div>
            )}

            {expectedRetentionTimeline.length > 1 && (
              <div className="visual-subsection retention-subsection">
                <h3>Expected Retention Timeline</h3>
                <div className="retention-metrics-grid">
                  <div className="retention-metric-card">
                    <span className="metric-label">Start Retention</span>
                    <span className="metric-value">{Number(firstRetention).toFixed(1)}%</span>
                  </div>
                  <div className="retention-metric-card">
                    <span className="metric-label">End Retention</span>
                    <span className="metric-value">{Number(lastRetention).toFixed(1)}%</span>
                  </div>
                  <div className="retention-metric-card">
                    <span className="metric-label">Estimated Drop</span>
                    <span className="metric-value">{estimatedDropPercent.toFixed(1)}%</span>
                  </div>
                </div>

                <div className="retention-chart-wrap" role="img" aria-label="Expected audience retention chart">
                  <svg viewBox="0 0 100 100" className="retention-chart-svg" preserveAspectRatio="none">
                    <line x1="0" y1="0" x2="0" y2="100" className="retention-axis" />
                    <line x1="0" y1="100" x2="100" y2="100" className="retention-axis" />
                    <line x1="0" y1="50" x2="100" y2="50" className="retention-grid" />
                    <polyline points={retentionPolylinePoints} className="retention-line" />
                  </svg>
                </div>

                {dropOffPoints.length > 0 && (
                  <div className="dropoff-list">
                    <h4>Predicted Drop-Off Points</h4>
                    <ul>
                      {dropOffPoints.slice(0, 8).map((point, idx) => (
                        <li key={`${point.timestamp}-${idx}`}>
                          <strong>{point.timestamp}</strong>: expected retention {Number(point.expected_retention_percent || 0).toFixed(1)}% ({point.reason})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {detectedObjects.length > 0 && (
              <div className="visual-subsection">
                <h3>Detected Objects</h3>
                <ul>
                  {detectedObjects.map((obj, i) => (
                    <li key={i}><strong>{obj.time}:</strong> {obj.object}</li>
                  ))}
                </ul>
              </div>
            )}
            {detectedActions.length > 0 && (
              <div className="visual-subsection">
                <h3>Detected Actions</h3>
                <ul>
                  {detectedActions.map((act, i) => (
                    <li key={i}><strong>{act.time}:</strong> {act.action}</li>
                  ))}
                </ul>
              </div>
            )}
            {(typeof monotonyScore === 'number' || typeof engagementScore === 'number') && (
              <div className="visual-subsection">
                <h3>Monotony & Engagement Scores</h3>
                <p><strong>Monotony Score:</strong> {monotonyScore}/10 (lower is better)</p>
                <p><strong>Engagement Score:</strong> {engagementScore}/10</p>
              </div>
            )}
            {suggestions.length > 0 && (
              <div className="visual-subsection">
                <h3>Time-Based Suggestions</h3>
                <ul>
                  {suggestions.map((sug, i) => (
                    <li key={i}><strong>{sug.time}:</strong> {sug.suggestion}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderCompetitiveLandscapeSection = () => {
    if (!hasCompetitiveLandscape) return null;
    const similarSources = Array.isArray(competitiveLandscapeData.similar_sources) ? competitiveLandscapeData.similar_sources : [];
    const contentGaps = Array.isArray(competitiveLandscapeData.content_gaps) ? competitiveLandscapeData.content_gaps : [];
    const opportunities = Array.isArray(competitiveLandscapeData.differentiation_opportunities) ? competitiveLandscapeData.differentiation_opportunities : [];

    return (
      <div className="section analysis-section" id="section-competitiveLandscape" data-section-id="competitiveLandscape">
        <div className="section-header" onClick={() => toggleSection('competitiveLandscape')}>
          <h2>Competitive Landscape</h2>
          <span className="toggle-icon" aria-hidden="true">{expandedSections.competitiveLandscape ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
        </div>
        {expandedSections.competitiveLandscape && (
          <div className="section-content">
            {competitiveLandscapeData.category && (
              <div className="research-subsection">
                <h3>Content Category</h3>
                <div className="research-card">
                  <p>{competitiveLandscapeData.category}</p>
                </div>
              </div>
            )}

            {similarSources.length > 0 && (
              <div className="research-subsection">
                <h3>Similar Sources</h3>
                {similarSources.map((source, idx) => (
                  <div key={idx} className="research-card">
                    <p><strong>{source.name}</strong> <span className="badge">{source.type}</span></p>
                    {source.url && <p><a href={source.url} target="_blank" rel="noopener noreferrer">{source.url}</a></p>}
                    {source.why_similar && <p><strong>Why similar:</strong> {source.why_similar}</p>}
                    {Array.isArray(source.known_for) && source.known_for.length > 0 && (
                      <div>
                        <strong>Known for:</strong>
                        <ul>{source.known_for.map((item, i) => <li key={i}>{item}</li>)}</ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {contentGaps.length > 0 && (
              <div className="research-subsection">
                <h3>Content Gaps</h3>
                {contentGaps.map((gap, idx) => (
                  <div key={idx} className="research-card">
                    <p><strong>Topic:</strong> {gap.topic}</p>
                    {Array.isArray(gap.covered_by) && gap.covered_by.length > 0 && (
                      <p><strong>Covered by:</strong> {gap.covered_by.join(', ')}</p>
                    )}
                    {gap.why_it_matters && <p><strong>Why it matters:</strong> {gap.why_it_matters}</p>}
                    {gap.how_to_incorporate && <p><strong>How to incorporate:</strong> {gap.how_to_incorporate}</p>}
                  </div>
                ))}
              </div>
            )}

            {opportunities.length > 0 && (
              <div className="research-subsection">
                <h3>Differentiation Opportunities</h3>
                {opportunities.map((opp, idx) => (
                  <div key={idx} className="research-card">
                    <p><strong>Angle:</strong> {opp.angle}</p>
                    {opp.rationale && <p><strong>Rationale:</strong> {opp.rationale}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderVisualBenchmarkSection = () => {
    if (!hasVisualBenchmark) return null;
    const benchmarkedVideos = Array.isArray(visualBenchmarkData.benchmarked_videos) ? visualBenchmarkData.benchmarked_videos : [];
    const visualGaps = Array.isArray(visualBenchmarkData.visual_gaps) ? visualBenchmarkData.visual_gaps : [];
    const pacingGaps = Array.isArray(visualBenchmarkData.pacing_gaps) ? visualBenchmarkData.pacing_gaps : [];
    const quickWins = Array.isArray(visualBenchmarkData.production_quick_wins) ? visualBenchmarkData.production_quick_wins : [];

    const impactClass = (level) => {
      if (!level) return '';
      const l = level.toLowerCase();
      if (l === 'high') return 'severity-high';
      if (l === 'medium') return 'severity-medium';
      return 'severity-low';
    };

    return (
      <div className="section analysis-section" id="section-visualBenchmark" data-section-id="visualBenchmark">
        <div className="section-header" onClick={() => toggleSection('visualBenchmark')}>
          <h2>Visual Benchmark</h2>
          <span className="toggle-icon" aria-hidden="true">{expandedSections.visualBenchmark ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
        </div>
        {expandedSections.visualBenchmark && (
          <div className="section-content">
            {benchmarkedVideos.length > 0 && (
              <div className="research-subsection">
                <h3>Benchmarked Videos</h3>
                {benchmarkedVideos.map((video, idx) => (
                  <div key={idx} className="research-card">
                    <p><strong>{video.title}</strong>{video.channel ? ` — ${video.channel}` : ''}</p>
                    {video.url && <p><a href={video.url} target="_blank" rel="noopener noreferrer">{video.url}</a></p>}
                    {video.visual_style_notes && <p><strong>Visual style:</strong> {video.visual_style_notes}</p>}
                    {video.pacing_notes && <p><strong>Pacing:</strong> {video.pacing_notes}</p>}
                    {Array.isArray(video.production_techniques) && video.production_techniques.length > 0 && (
                      <div>
                        <strong>Production techniques:</strong>
                        <ul>{video.production_techniques.map((t, i) => <li key={i}>{t}</li>)}</ul>
                      </div>
                    )}
                    {video.transcript_available === false && (
                      <p className="muted-note">Transcript unavailable — based on web research</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {visualGaps.length > 0 && (
              <div className="research-subsection">
                <h3>Visual Gaps</h3>
                {visualGaps.map((gap, idx) => (
                  <div key={idx} className={`research-card ${impactClass(gap.impact)}`}>
                    <p><strong>Gap:</strong> {gap.gap}</p>
                    {Array.isArray(gap.seen_in) && gap.seen_in.length > 0 && (
                      <p><strong>Seen in:</strong> {gap.seen_in.join(', ')}</p>
                    )}
                    {gap.impact && <p><strong>Impact:</strong> <span className="badge">{gap.impact}</span></p>}
                    {gap.how_to_fix && <p><strong>How to fix:</strong> {gap.how_to_fix}</p>}
                  </div>
                ))}
              </div>
            )}

            {pacingGaps.length > 0 && (
              <div className="research-subsection">
                <h3>Pacing Gaps</h3>
                {pacingGaps.map((pg, idx) => (
                  <div key={idx} className="research-card">
                    <p><strong>Issue:</strong> {pg.issue}</p>
                    {pg.benchmark_example && <p><strong>Benchmark example:</strong> {pg.benchmark_example}</p>}
                    {pg.recommendation && <p><strong>Recommendation:</strong> {pg.recommendation}</p>}
                  </div>
                ))}
              </div>
            )}

            {quickWins.length > 0 && (
              <div className="research-subsection">
                <h3>Production Quick Wins</h3>
                {quickWins.map((win, idx) => (
                  <div key={idx} className="research-card">
                    <p><strong>{win.technique}</strong></p>
                    <p>
                      <span className="badge">Effort: {win.effort}</span>{' '}
                      <span className="badge">Impact: {win.expected_impact}</span>
                    </p>
                    {win.implementation_tip && <p>{win.implementation_tip}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  /* The verdict: one score, one list.
   *
   * Everything the review found about THIS SCRIPT arrives here already merged.
   * Clarity, claims risk, trigger sensitivity, research, CTA alignment and
   * persona confusion are folded into these actions server-side rather than
   * rendered as six more sections the reader has to reconcile. Their own
   * sections are gone for exactly that reason: a creator reading six lists is
   * doing the merge themselves, and doing it worse than the agent that had all
   * six in front of it.
   *
   * What still renders beside this is what is NOT a verdict on the writing --
   * the generated metadata, the readings taken from the footage, shot
   * direction, and the market context.
   */
  const renderVerdictSection = () => {
    if (!hasStructure) return null;

    const score = structureData.score || {};
    const actions = Array.isArray(structureData.actions) ? structureData.actions : [];
    const reasons = Array.isArray(score.adjustment_reasons) ? score.adjustment_reasons : [];
    const stretch = structureData.longest_unbroken_stretch;
    const adjustment = Number(score.adjustment || 0);

    const scoreColor = (value) => {
      if (value >= 80) return '#4caf50';
      if (value >= 50) return '#ff9800';
      return '#f44336';
    };

    return (
      <div className="section analysis-section" id="section-verdict" data-section-id="verdict">
        <div className="section-header" onClick={() => toggleSection('verdict')}>
          <h2>The Verdict</h2>
          <span className="toggle-icon" aria-hidden="true">{expandedSections.verdict ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
        </div>

        {expandedSections.verdict && (
          <div className="section-content">
            <div className="score-card">
              <div className="score-label">RETENTION</div>
              <div className="score-value" style={{ color: scoreColor(score.normalized_score) }}>
                {score.normalized_score}/100
              </div>
              {/* The scale needs its own context: it runs low by design, so a
                  middling number is a normal video rather than a broken one. */}
              <p className="improvement-issue">
                How well this is built to hold attention, and whether it stands up.
                The scale runs low — most videos land well under halfway.
              </p>

              {/* The only place the score is broken down. A creator looking at
                  61 where the structure scored 74 is owed the sentence saying
                  where the other 13 went. */}
              {adjustment < 0 && (
                <div className="verdict-adjustment">
                  <p className="improvement-issue">
                    <strong>{score.structural_score}</strong> on structure,
                    {' '}<strong>{adjustment}</strong> for what the review found:
                  </p>
                  <ul>
                    {reasons.map((reason, index) => <li key={index}>{reason}</li>)}
                  </ul>
                </div>
              )}
            </div>

            {actions.length > 0 && (
              <div className="improvements-section">
                <h3>What to change</h3>
                {actions.map((action, index) => (
                  <div key={index} className={`improvement-card priority-${action.priority || 3}`}>
                    {action.timestamp && (
                      <div className="improvement-header">
                        <span className="impact-badge">{action.timestamp}</span>
                      </div>
                    )}
                    <p className="improvement-suggestion"><strong>{action.action}</strong></p>
                    {action.why && <p className="improvement-issue">{action.why}</p>}
                    {action.rewritten && (
                      <>
                        {action.current && (
                          <p className="improvement-issue"><em>“{action.current}”</em></p>
                        )}
                        <p className="improvement-suggestion">
                          <strong>Say instead:</strong> “{action.rewritten}”
                        </p>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            {stretch?.seconds ? (
              <div className="improvement-card priority-2">
                <h4>{stretch.seconds}s on one thread without a break</h4>
                <p className="improvement-issue">
                  <strong>{stretch.start}–{stretch.end}:</strong> {stretch.why_it_matters}
                </p>
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="structured-analysis-layout">
      {sectionItems.length > 0 && (
        <aside className="analysis-sidebar">
          <h3>Sections</h3>
          <nav>
            {sectionItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`sidebar-link ${activeSection === item.id ? 'active' : ''}`}
                onClick={() => handleSidebarClick(item.id)}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </aside>
      )}

      <div className="structured-analysis-container">
        {agentErrorEntries.length > 0 && (
          <div className="section analysis-section" id="section-agentErrors" data-section-id="agentErrors">
            <div className="section-header">
              <h2>Analysis Warnings</h2>
            </div>
            <div className="section-content">
              <p>Some analysis agents did not return usable results, so their sections may be missing or incomplete:</p>
              <ul>
                {agentErrorEntries.map(([key, message]) => (
                  <li key={key}>
                    <strong>{key.replace(/_/g, ' ')}:</strong> {String(message)}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {renderVerdictSection()}
        {renderMetadataSection()}
        {renderVisualSection()}
        {renderVisualSuggestionsSection()}
        {renderCompetitiveLandscapeSection()}
        {renderVisualBenchmarkSection()}
      </div>
    </div>
  );
};

export default StructuredAnalysisView;
